/**
 * BatchSurface — embedded batch list for the Entity Drilldown.
 *
 * Pass 21 left the drilldown showing stages 1-5 as "completed/current/locked"
 * derived from `currentStageNumber`, but the user had to jump to the Batches
 * tab to actually see (or edit) batch-level data. This pass surfaces the
 * batches inline, above the stage-cluster stack.
 *
 * Variant rules:
 *   - Variant B (PART, no sub-parts): the part's own batches.
 *   - Variant A (PART with sub-parts): a friendly hint pointing to each
 *     sub-part — batches live there, not on the parent.
 *   - Sub-part (SUB_PART): the sub-part's own batches.
 *
 * Inline editing: each batch row exposes the 5 procurement-stage date inputs
 * (gated on predecessor presence), plus admin-only Cancel / Replace buttons.
 * Mirrors the pattern from project-batches-tab but compact for the drilldown.
 */

import { useState } from 'react';
import { Ban, Package, Plus, Repeat } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { todayPktIsoDate } from '@procurement/shared';
import { AttachmentBadge } from '@/components/attachments/attachment-badge';
import { ReplaceBatchModal } from '@/components/replace-batch-modal';
import { DmyDateInput } from '@/components/forms/dmy-date-input';
import { FormField, Input } from '@/components/ui/form-field';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { cn } from '@/lib/cn';
import {
  useBatches,
  useCancelBatch,
  useCreateBatch,
  useSuppliersList,
  useUpdateBatch,
  type OrderBatch,
  type ProjectItem,
} from '@/lib/pipeline-queries';
import type { Project } from '@/lib/project-queries';
import { useCurrentUser } from '@/lib/auth';
import { useAttachmentCounts } from '@/lib/attachments-queries';
import { isLockedForUser } from '@/lib/closure-lock';

interface BatchSurfaceProps {
  project: Project;
  item: ProjectItem;
  /** When item is Variant A (PART with sub-parts), pass the live sub-parts. */
  subParts?: readonly ProjectItem[];
}

export function BatchSurface({ project, item, subParts }: BatchSurfaceProps) {
  // Variant A: redirect the user to the sub-parts — the parent has no batches.
  if (item.itemType === 'PART' && item.hasSubParts) {
    return (
      <div className="rounded-md border border-border-default bg-surface p-4">
        <div className="mb-2 text-sm font-medium text-primary">Order batches</div>
        <p className="text-xs text-tertiary">
          This part has sub-parts. Each sub-part carries its own batches — click a sub-part below
          to view its procurement timeline.
        </p>
        {subParts && subParts.length > 0 ? (
          <ul className="mt-3 flex flex-wrap gap-2">
            {subParts.map((sp) => (
              <li key={sp.id}>
                <Link
                  to="/projects/$projectId/items/$itemId"
                  params={{ projectId: project.id, itemId: sp.id }}
                  className="inline-flex items-center rounded-sm border border-border-default bg-base/40 px-2 py-1 text-xs text-secondary hover:border-accent hover:text-accent-strong"
                >
                  {sp.name}
                  <span className="ml-1 text-secondary">· {sp.activeOrderedQuantity} ordered</span>
                </Link>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  }

  return <DirectBatchList project={project} item={item} />;
}

// ---------------------------------------------------------------------------
// Direct batch list (Variant B / sub-part)
// ---------------------------------------------------------------------------

function DirectBatchList({ project, item }: { project: Project; item: ProjectItem }) {
  const batchesQ = useBatches(project.id, item.id);
  const me = useCurrentUser();
  const isAdmin = me.data?.user.role === 'ADMIN';
  const [showCreate, setShowCreate] = useState(false);
  // E26 — per-batch attachment counts
  const counts = useAttachmentCounts(project.id);

  const batches = batchesQ.data ?? [];
  // Spec §9 — closed projects lock edits against Assistants only.
  const locked = isLockedForUser(project, isAdmin);
  const canCreate = !locked; // server also enforces FINALISED workflow state

  // Spec §7.5 — Total active ordered > required qty → soft warning
  // ("over-ordered by N units"), allowed. The gate doesn't reject the order;
  // we just inform the user. `activeOrderedQuantity` on the item is the
  // canonical sum for live ACTIVE batches.
  const overOrderedBy =
    item.totalRequiredQuantity > 0 && item.activeOrderedQuantity > item.totalRequiredQuantity
      ? item.activeOrderedQuantity - item.totalRequiredQuantity
      : 0;

  return (
    <div className="rounded-md border border-border-default bg-surface">
      <div className="flex items-center justify-between border-b border-border-default px-4 py-2">
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-tertiary" strokeWidth={1.5} />
          <span className="text-sm font-medium text-primary">Order batches</span>
          <span className="text-xs text-secondary">
            {batches.filter((b) => b.status === 'ACTIVE').length} active · {batches.length} total
          </span>
          {overOrderedBy > 0 ? (
            <span
              className="inline-flex items-center rounded-sm border border-status-warning/40 bg-status-warning/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-status-warning"
              title={`Active ordered (${item.activeOrderedQuantity}) exceeds required (${item.totalRequiredQuantity})`}
            >
              Over-ordered by {overOrderedBy}
            </span>
          ) : null}
        </div>
        {canCreate ? (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1 rounded-sm border border-border-default bg-surface px-2 py-1 text-xs text-secondary hover:border-accent hover:text-accent-strong"
          >
            <Plus className="h-3 w-3" strokeWidth={1.5} />
            Add batch
          </button>
        ) : null}
      </div>

      {batches.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-tertiary">
          No batches yet. Add one to start the procurement pipeline.
        </div>
      ) : (
        <div className="divide-y divide-border-subtle">
          {batches.map((batch) => (
            <BatchRow
              key={batch.id}
              project={project}
              item={item}
              batch={batch}
              isAdmin={Boolean(isAdmin)}
              attachmentCount={counts.data?.byBatch[batch.id] ?? 0}
            />
          ))}
        </div>
      )}

      {showCreate ? (
        <CreateBatchInline
          projectId={project.id}
          itemId={item.id}
          onClose={() => setShowCreate(false)}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single batch row — compact procurement-stage timeline
// ---------------------------------------------------------------------------

function BatchRow({
  project,
  item,
  batch,
  isAdmin,
  attachmentCount,
}: {
  project: Project;
  item: ProjectItem;
  batch: OrderBatch;
  isAdmin: boolean;
  attachmentCount: number;
}) {
  const update = useUpdateBatch(project.id, item.id);
  const cancel = useCancelBatch(project.id, item.id);
  const [showReplace, setShowReplace] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Spec §9 — closed projects lock edits against Assistants only.
  // Batch-inactive (CANCELLED/REPLACED) is a separate lock that applies to everyone.
  const projectLocked = isLockedForUser(project, isAdmin);
  const batchInactive = batch.status !== 'ACTIVE';
  const locked = batchInactive || projectLocked;

  // Patch E14 — spec §7.5: Pre-shipment and Transit are OPTIONAL stages whose
  // absence does not gate later stages. Each of stages 2-4 only requires the
  // batch to exist (i.e. orderPlacedDate set). In-house Inspection (stage 5)
  // is the only one that legitimately requires its predecessor (Arrived) since
  // §7.5 says "In-house Inspection date — set after Arrived." Pre-E14 the UI
  // chained Transit on Pre-ship and Arrived on Transit, blocking the legitimate
  // case where the operator skips one or both optional inspections.
  const stages = [
    { num: 1, label: 'Placed', field: 'orderPlacedDate' as const, value: batch.orderPlacedDate, enabled: true },
    { num: 2, label: 'Pre-ship', field: 'preShipmentInspectionDate' as const, value: batch.preShipmentInspectionDate, enabled: Boolean(batch.orderPlacedDate) },
    { num: 3, label: 'Transit', field: 'inTransitDate' as const, value: batch.inTransitDate, enabled: Boolean(batch.orderPlacedDate) },
    { num: 4, label: 'Arrived', field: 'arrivedDate' as const, value: batch.arrivedDate, enabled: Boolean(batch.orderPlacedDate) },
    { num: 5, label: 'In-house', field: 'inHouseInspectionDate' as const, value: batch.inHouseInspectionDate, enabled: Boolean(batch.arrivedDate) },
  ];

  function onUpdate(field: typeof stages[number]['field'], value: string) {
    update.mutate({ batchId: batch.id, rowVersion: batch.rowVersion, [field]: value || null });
  }

  return (
    <div className="px-4 py-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium text-primary">{batch.supplierNameSnapshot}</span>
          <span className="text-secondary">{batch.quantity} units</span>
          {/* E26 — batch-level attachment indicator */}
          <AttachmentBadge count={attachmentCount} />
          {batch.status !== 'ACTIVE' ? (
            <span
              className={cn(
                'inline-flex items-center rounded-sm border px-1.5 py-0.5 text-xs font-medium',
                batch.status === 'CANCELLED'
                  ? 'border-danger/30 bg-danger/10 text-danger-strong'
                  : 'border-tertiary/30 bg-subtle text-tertiary',
              )}
            >
              {batch.status}
            </span>
          ) : null}
          {batch.isOverdue ? (
            <span className="inline-flex items-center rounded-sm border border-warning/30 bg-warning/10 px-1.5 py-0.5 text-xs font-medium text-warning-strong">
              Overdue
            </span>
          ) : null}
        </div>
        {batch.status === 'ACTIVE' && isAdmin && !projectLocked ? (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setShowReplace(true)}
              className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs text-tertiary hover:bg-subtle hover:text-secondary"
            >
              <Repeat className="h-3 w-3" strokeWidth={1.5} />
              Replace
            </button>
            <button
              type="button"
              onClick={() => setShowCancel(true)}
              className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs text-tertiary hover:bg-subtle hover:text-danger-strong"
            >
              <Ban className="h-3 w-3" strokeWidth={1.5} />
              Cancel
            </button>
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-5 gap-2">
        {stages.map((s) => (
          <div key={s.num}>
            <label className="block text-[10px] font-semibold uppercase tracking-wide text-tertiary">
              {s.num}. {s.label}
            </label>
            <DmyDateInput
              value={s.value ?? ''}
              disabled={locked || !s.enabled}
              onChange={(value) => onUpdate(s.field, value)}
              showValidationMessage={false}
              className="mt-0.5 h-7 px-1.5 py-1 text-xs"
            />
          </div>
        ))}
      </div>

      {update.error ? (
        <div className="mt-1 text-xs text-danger-strong">{update.error.message}</div>
      ) : null}

      {showReplace ? (
        <ReplaceBatchModal
          open
          onClose={() => setShowReplace(false)}
          projectId={project.id}
          itemId={item.id}
          batch={batch}
        />
      ) : null}

      {showCancel ? (
        <CancelBatchInline
          pending={cancel.isPending}
          error={cancel.error?.message ?? null}
          reason={cancelReason}
          onChangeReason={setCancelReason}
          onCancel={() => {
            setShowCancel(false);
            setCancelReason('');
          }}
          onConfirm={() => {
            if (!cancelReason.trim()) return;
            cancel.mutate(
              {
                batchId: batch.id,
                cancellationReason: cancelReason.trim(),
                rowVersion: batch.rowVersion,
              },
              {
                onSuccess: () => {
                  setShowCancel(false);
                  setCancelReason('');
                },
              },
            );
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline create-batch form (popover-style, compact)
//
// Patch P3 — was a raw <select> + unlabeled <input>s. Now uses the same
// SearchableSelect + FormField primitives the Batches-tab modal does, so the
// two entry points (project > batches > New, and project > part drilldown >
// New batch) feel identical.
// ---------------------------------------------------------------------------

function CreateBatchInline({
  projectId,
  itemId,
  onClose,
}: {
  projectId: string;
  itemId: string;
  onClose: () => void;
}) {
  const create = useCreateBatch(projectId, itemId);
  const suppliers = useSuppliersList();
  const [supplierId, setSupplierId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [orderPlacedDate, setOrderPlacedDate] = useState(todayPktIsoDate()); // Patch E18 — PKT date default
  const [expectedArrivalDate, setExpectedArrivalDate] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    const qty = Number(quantity);
    if (!supplierId) return setError('Supplier is required');
    if (!Number.isFinite(qty) || qty <= 0) return setError('Quantity must be positive');
    if (!orderPlacedDate || !expectedArrivalDate) return setError('Both dates are required');
    if (expectedArrivalDate < orderPlacedDate) return setError('ETA must be after order placed');
    try {
      await create.mutateAsync({
        supplierId,
        quantity: qty,
        orderPlacedDate,
        expectedArrivalDate,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed');
    }
  }

  const supplierItems = (suppliers.data ?? [])
    .filter((s) => s.isActive)
    .map((s) => ({ value: s.id, label: s.name }));

  return (
    <div className="border-t border-border-default bg-subtle/30 px-4 py-3">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <FormField label="Supplier" required>
          <SearchableSelect
            items={supplierItems}
            value={supplierId || null}
            onChange={(v) => setSupplierId(v ?? '')}
            placeholder="Select a supplier…"
          />
        </FormField>
        <FormField label="Quantity" required>
          <Input
            type="number"
            min={1}
            step={1}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
        </FormField>
        <FormField label="Order placed" required>
          <DmyDateInput
            value={orderPlacedDate}
            onChange={setOrderPlacedDate}
          />
        </FormField>
        <FormField label="Expected arrival" required>
          <DmyDateInput
            value={expectedArrivalDate}
            onChange={setExpectedArrivalDate}
          />
        </FormField>
      </div>
      {error ? (
        <div className="mt-2 text-xs text-danger-strong">{error}</div>
      ) : null}
      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={create.isPending}
          className="rounded-sm border border-border-default bg-surface px-2.5 py-1 text-xs text-secondary hover:border-border-strong disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={create.isPending}
          className="rounded-sm bg-accent px-2.5 py-1 text-xs font-medium text-white hover:bg-accent-strong disabled:opacity-50"
        >
          {create.isPending ? 'Adding…' : 'Add batch'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline cancel-batch confirm (compact, no modal — fits the drilldown row)
// ---------------------------------------------------------------------------

function CancelBatchInline({
  pending,
  error,
  reason,
  onChangeReason,
  onCancel,
  onConfirm,
}: {
  pending: boolean;
  error: string | null;
  reason: string;
  onChangeReason: (s: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="mt-2 rounded-sm border border-danger/30 bg-danger/5 px-3 py-2">
      <div className="text-xs font-medium text-danger-strong">Cancel this batch?</div>
      <textarea
        rows={2}
        value={reason}
        onChange={(e) => onChangeReason(e.target.value)}
        placeholder="Cancellation reason (required, captured in audit)"
        className="mt-1 w-full resize-none rounded-sm border border-border-default bg-surface px-2 py-1 text-xs text-primary focus:border-accent focus:outline-none"
      />
      {error ? <div className="mt-1 text-xs text-danger-strong">{error}</div> : null}
      <div className="mt-2 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={pending}
          className="rounded-sm border border-border-default bg-surface px-2 py-1 text-xs text-secondary hover:border-border-strong disabled:opacity-50"
        >
          Keep
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={pending || !reason.trim()}
          className="rounded-sm bg-danger px-2.5 py-1 text-xs font-medium text-white hover:bg-danger-strong disabled:opacity-50"
        >
          {pending ? 'Cancelling…' : 'Cancel batch'}
        </button>
      </div>
    </div>
  );
}
